import React, { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import styled from "styled-components"
import { Label, Error } from "./input-v2.styled"
import PropTypes from "prop-types"

const InputStyle = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative;

  .form-input {
    width: 100%;
    padding: 14px 10px;
    box-sizing: border-box;
    font-family: system-ui;
    font-size: 16px;
    font-weight: 400;
    border-radius: 8px;
    border-width: 1px;
    height: 42px;
    border: 1px solid #d0d5dd;
    padding-right: 48px; /* Space for the eye icon */

    ::placeholder {
      color: ${props => props.theme.gray500};
    }
  }

  .input-error {
    border-color: red;
    border-radius: 4px;
  }
`

const EyeIcon = styled.span`
  position: absolute;
  right: 14px;
  bottom: 0;
  transform: translateY(-6px);
  cursor: pointer;
  font-size: 18px;
  color: ${props => props.theme.gray700 || "#999"};

  &:hover {
    color: ${props => props.theme.gray900 || "#333"};
  }
`

const InputPassword = ({
  label,
  name,
  validate,
  required,
  defaultValue = "",
  placeholder = "Enter your password",
}) => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext()

  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    setValue(name, defaultValue)
  }, [name, defaultValue, setValue])

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  return (
    <InputStyle>
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}

      <input
        className={`form-input ${errors[name] ? "input-error" : ""}`}
        id={name}
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        {...register(name, { validate })}
      />

      <EyeIcon onClick={togglePasswordVisibility}>
        {showPassword ? <Visible /> : <Visible />}
      </EyeIcon>

      {errors[name] && <Error>{errors[name].message}</Error>}
    </InputStyle>
  )
}

export default InputPassword

InputPassword.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  validate: PropTypes.func,
  required: PropTypes.bool,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
}

const Visible = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <g clipPath="url(#clip0_8121_444)">
        <path
          d="M19.3923 7.84907C18.0998 5.74407 15.1598 2.2124 9.9998 2.2124C4.8398 2.2124 1.8998 5.74407 0.607299 7.84907C0.207739 8.49532 -0.00390625 9.2401 -0.00390625 9.9999C-0.00390625 10.7597 0.207739 11.5045 0.607299 12.1507C1.8998 14.2557 4.8398 17.7874 9.9998 17.7874C15.1598 17.7874 18.0998 14.2557 19.3923 12.1507C19.7919 11.5045 20.0035 10.7597 20.0035 9.9999C20.0035 9.2401 19.7919 8.49532 19.3923 7.84907ZM17.9715 11.2782C16.8615 13.0832 14.349 16.1207 9.9998 16.1207C5.65063 16.1207 3.13813 13.0832 2.02813 11.2782C1.79074 10.8941 1.66501 10.4515 1.66501 9.9999C1.66501 9.54834 1.79074 9.1057 2.02813 8.72157C3.13813 6.91657 5.65063 3.87907 9.9998 3.87907C14.349 3.87907 16.8615 6.91324 17.9715 8.72157C18.2089 9.1057 18.3346 9.54834 18.3346 9.9999C18.3346 10.4515 18.2089 10.8941 17.9715 11.2782Z"
          fill="#667085"
        />
        <path
          d="M9.99968 5.83319C9.17559 5.83319 8.37 6.07756 7.6848 6.5354C6.9996 6.99324 6.46554 7.64398 6.15018 8.40534C5.83481 9.1667 5.7523 10.0045 5.91307 10.8127C6.07384 11.621 6.47068 12.3634 7.0534 12.9461C7.63612 13.5289 8.37855 13.9257 9.1868 14.0865C9.99505 14.2472 10.8328 14.1647 11.5942 13.8494C12.3555 13.534 13.0063 12.9999 13.4641 12.3147C13.922 11.6295 14.1663 10.8239 14.1663 9.99986C14.165 8.8952 13.7256 7.83616 12.9445 7.05504C12.1634 6.27393 11.1043 5.83451 9.99968 5.83319ZM9.99968 12.4999C9.50522 12.4999 9.02187 12.3532 8.61075 12.0785C8.19963 11.8038 7.8792 11.4134 7.68998 10.9566C7.50076 10.4998 7.45125 9.99708 7.54771 9.51213C7.64418 9.02718 7.88228 8.58172 8.23191 8.23209C8.58154 7.88246 9.027 7.64436 9.51195 7.54789C9.9969 7.45143 10.4996 7.50094 10.9564 7.69016C11.4132 7.87938 11.8036 8.19981 12.0783 8.61093C12.3531 9.02205 12.4997 9.5054 12.4997 9.99986C12.4997 10.6629 12.2363 11.2988 11.7674 11.7676C11.2986 12.2365 10.6627 12.4999 9.99968 12.4999Z"
          fill="#667085"
        />
      </g>
      <defs>
        <clipPath id="clip0_8121_444">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}
